import { ScreenOrientation } from "@capacitor/screen-orientation";
import { StatusBar, Style } from "@capacitor/status-bar";
import { Box, ThemeProvider } from "@mui/material";
import { type ReactElement, useEffect } from "react";
import { Route, Switch, useHistory } from "react-router-dom";

import { berlinTheme } from "../appV2/redesign/theme/theme";
import { MobileBottomNav } from "./components/MobileBottomNav";
import { MobileProfile } from "./Profile/MobileProfile";
import { Platform } from "./utils/platform";

/**
 * Mobile app shell - renders for all /mobile/* routes
 * Configures native mobile settings and provides bottom navigation
 */
export function MobileApp(): ReactElement {
  const history = useHistory();

  useEffect(() => {
    if (!Platform.isMobile) {
      // Not in mobile app - could redirect to desktop version
      return;
    }

    // Configure mobile settings
    void (async () => {
      try {
        // Lock to portrait orientation
        await ScreenOrientation.lock({ orientation: "portrait" });

        // Configure status bar
        if (Platform.isIOS) {
          await StatusBar.setStyle({ style: Style.Dark });
        } else if (Platform.isAndroid) {
          await StatusBar.setStyle({ style: Style.Dark });
          await StatusBar.setBackgroundColor({ color: "#1976d2" });
        }
      } catch {
        // Silent fail - these features may not be available in all contexts
      }
    })();
  }, [history]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        maxHeight: "-webkit-fill-available", // iOS Safari fix
        overflow: "hidden",
        backgroundColor: "background.default", // Prevent white gaps
      }}
    >
      {/* Safe area spacer at top - doesn't scroll, keeps navbar at bottom */}
      <Box
        sx={{
          height: "env(safe-area-inset-top, 0px)",
          flexShrink: 0,
          backgroundColor: "background.default",
        }}
      />

      {/* Main content area - takes remaining space and scrolls */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          WebkitOverflowScrolling: "touch",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            minHeight: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Switch>
            <Route exact path="/mobile/profile">
              <ThemeProvider theme={berlinTheme}>
                <MobileProfile />
              </ThemeProvider>
            </Route>
          </Switch>
        </Box>
      </Box>

      {/* Bottom navigation - auto height to accommodate safe area */}
      <Box
        sx={{
          minHeight: "56px",
          paddingBottom: "env(safe-area-inset-bottom, 0px)", // Home indicator
          backgroundColor: "background.paper",
          flexShrink: 0,
        }}
      >
        <MobileBottomNav />
      </Box>
    </Box>
  );
}
